/**
 * VW Client Contacts Summary Service
 * Service for accessing the vwClientContactsSummary view API endpoints
 * 
 * This service provides a comprehensive view of client contacts with their
 * associated contact type tags, including aggregated tag data in both CSV
 * and JSON formats.
 */

import { BaseService } from './BaseService';
import { parseUTCDate, formatLocalDateTime, formatTimeAgo } from '../utils/dateUtils';

/**
 * Helper class for working with client contacts summary
 */
export class VWClientContactsSummaryHelper {
    /**
     * Parse tags JSON string to array of tag objects
     * @param {string} tagsJson - JSON string of tags
     * @returns {Array} Array of tag objects
     */
    static parseTagsJson(tagsJson) {
        if (!tagsJson) return [];
        try {
            return JSON.parse(tagsJson);
        } catch (error) {
            console.error('Failed to parse tags JSON:', error);
            return [];
        }
    }

    /**
     * Get tags as array of strings
     * @param {Object} contact - Contact object
     * @returns {Array<string>} Array of tag names
     */
    static getTagsArray(contact) {
        if (contact.tagsCsv) {
            return contact.tagsCsv.split(',').map(tag => tag.trim()).filter(Boolean);
        }
        const tags = this.parseTagsJson(contact.tagsJson);
        return tags.map(tag => tag.ContactType);
    }

    /**
     * Check if contact has tags
     * @param {Object} contact - Contact object
     * @returns {boolean}
     */
    static hasTags(contact) {
        return (contact.tagCount || 0) > 0;
    }

    /**
     * Get full name formatted
     * @param {Object} contact - Contact object
     * @returns {string}
     */
    static getFullName(contact) {
        return contact.fullName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Unknown Contact';
    }

    /**
     * Get full address formatted
     * @param {Object} contact - Contact object
     * @returns {string}
     */
    static getFullAddress(contact) {
        const parts = [];
        
        if (contact.address1) parts.push(contact.address1);
        if (contact.address2) parts.push(contact.address2);
        
        const cityStateZip = [];
        if (contact.city) cityStateZip.push(contact.city);
        if (contact.stateCode) cityStateZip.push(contact.stateCode);
        if (contact.zip) cityStateZip.push(contact.zip);
        
        if (cityStateZip.length > 0) {
            parts.push(cityStateZip.join(', '));
        }
        
        return parts.join(', ') || 'No address';
    }

    /**
     * Format date for display
     * @param {string} dateString - UTC date string from backend
     * @returns {string} Formatted date in local time
     */
    static formatDate(dateString) {
        if (!dateString) return 'N/A';
        return formatLocalDateTime(dateString);
    }

    /**
     * Get relative time (e.g., "2 hours ago")
     * @param {string} dateString - UTC date string from backend
     * @returns {string} Relative time string
     */
    static getRelativeTime(dateString) {
        if (!dateString) return 'Unknown';
        return formatTimeAgo(dateString);
    }

    /**
     * Get status badges for a contact
     * @param {Object} contact - Contact object
     * @returns {Array<Object>} Array of badge objects with label and type
     */
    static getStatusBadges(contact) {
        const badges = [];
        
        if (contact.isActive) {
            badges.push({ label: 'Active', type: 'success' });
        } else {
            badges.push({ label: 'Inactive', type: 'danger' });
        }
        
        if (contact.isSubscribed) {
            badges.push({ label: 'Subscribed', type: 'info' });
        }
        
        if (contact.clientPortalAccess) {
            badges.push({ label: 'Portal Access', type: 'primary' });
        }
        
        return badges;
    }

    /**
     * Group contacts by tag
     * @param {Array} contacts - Array of contacts
     * @returns {Object} Contacts grouped by tag name
     */
    static groupByTag(contacts) {
        if (!Array.isArray(contacts)) return {};
        
        const groups = {};
        
        contacts.forEach(contact => {
            const tags = this.getTagsArray(contact);
            
            if (tags.length === 0) {
                if (!groups['Untagged']) {
                    groups['Untagged'] = [];
                }
                groups['Untagged'].push(contact);
            } else {
                tags.forEach(tag => {
                    if (!groups[tag]) {
                        groups[tag] = [];
                    }
                    groups[tag].push(contact);
                });
            }
        });
        
        return groups;
    }

    /**
     * Group contacts by city
     * @param {Array} contacts - Array of contacts
     * @returns {Object} Contacts grouped by city
     */
    static groupByCity(contacts) {
        if (!Array.isArray(contacts)) return {};
        
        return contacts.reduce((acc, contact) => {
            const city = contact.city || 'Unknown';
            if (!acc[city]) {
                acc[city] = [];
            }
            acc[city].push(contact);
            return acc;
        }, {});
    }

    /**
     * Group contacts by state
     * @param {Array} contacts - Array of contacts
     * @returns {Object} Contacts grouped by state
     */
    static groupByState(contacts) {
        if (!Array.isArray(contacts)) return {};
        
        return contacts.reduce((acc, contact) => {
            const state = contact.stateCode || 'Unknown';
            if (!acc[state]) {
                acc[state] = [];
            }
            acc[state].push(contact);
            return acc;
        }, {});
    }

    /**
     * Filter contacts by status
     * @param {Array} contacts - Array of contacts
     * @param {Object} filters - Filter options (isActive, isSubscribed, clientPortalAccess)
     * @returns {Array} Filtered contacts
     */
    static filterByStatus(contacts, filters = {}) {
        if (!Array.isArray(contacts)) return [];
        
        return contacts.filter(contact => {
            if (filters.isActive !== undefined && contact.isActive !== filters.isActive) {
                return false;
            }
            if (filters.isSubscribed !== undefined && contact.isSubscribed !== filters.isSubscribed) {
                return false;
            }
            if (filters.clientPortalAccess !== undefined && contact.clientPortalAccess !== filters.clientPortalAccess) {
                return false;
            }
            return true;
        });
    }

    /**
     * Sort contacts
     * @param {Array} contacts - Array of contacts
     * @param {string} sortBy - Sort field ('name', 'email', 'tagCount', 'lastTagged')
     * @param {string} order - Sort order ('asc' or 'desc')
     * @returns {Array} Sorted contacts
     */
    static sortContacts(contacts, sortBy = 'name', order = 'asc') {
        if (!Array.isArray(contacts)) return [];
        
        const sorted = [...contacts];
        sorted.sort((a, b) => {
            let comparison = 0;
            
            switch (sortBy) {
                case 'name':
                    comparison = (a.fullName || '').localeCompare(b.fullName || '');
                    break;
                case 'lastName':
                    comparison = (a.lastName || '').localeCompare(b.lastName || '') ||
                                (a.firstName || '').localeCompare(b.firstName || '');
                    break;
                case 'email':
                    comparison = (a.email || '').localeCompare(b.email || '');
                    break;
                case 'tagCount':
                    comparison = (a.tagCount || 0) - (b.tagCount || 0);
                    break;
                case 'lastTagged': {
                    const dateA = parseUTCDate(a.lastTaggedAt);
                    const dateB = parseUTCDate(b.lastTaggedAt);
                    const timeA = dateA ? dateA.getTime() : 0;
                    const timeB = dateB ? dateB.getTime() : 0;
                    comparison = timeA - timeB;
                    break;
                }
                case 'created': {
                    const dateA = parseUTCDate(a.contactCreatedAt);
                    const dateB = parseUTCDate(b.contactCreatedAt);
                    const timeA = dateA ? dateA.getTime() : 0;
                    const timeB = dateB ? dateB.getTime() : 0;
                    comparison = timeA - timeB;
                    break;
                }
                default:
                    comparison = 0;
            }
            
            return order === 'asc' ? comparison : -comparison;
        });
        
        return sorted;
    }

    /**
     * Get contact statistics
     * @param {Array} contacts - Array of contacts
     * @returns {Object} Statistics object
     */
    static getContactStatistics(contacts) {
        if (!Array.isArray(contacts)) {
            return {
                total: 0,
                active: 0,
                inactive: 0,
                subscribed: 0,
                withPortalAccess: 0,
                withTags: 0,
                withoutTags: 0,
                uniqueTags: []
            };
        }

        const total = contacts.length;
        const active = contacts.filter(c => c.isActive).length;
        const inactive = total - active;
        const subscribed = contacts.filter(c => c.isSubscribed).length;
        const withPortalAccess = contacts.filter(c => c.clientPortalAccess).length;
        const withTags = contacts.filter(c => this.hasTags(c)).length;
        const withoutTags = total - withTags;
        
        // Get unique tags
        const tagsSet = new Set();
        contacts.forEach(contact => {
            const tags = this.getTagsArray(contact);
            tags.forEach(tag => tagsSet.add(tag));
        });
        const uniqueTags = Array.from(tagsSet).sort();

        return {
            total,
            active,
            inactive,
            subscribed,
            withPortalAccess,
            withTags,
            withoutTags,
            uniqueTags,
            uniqueTagsCount: uniqueTags.length
        };
    }
}

/**
 * VW Client Contacts Summary Service Class
 */
export class VWClientContactsSummaryService extends BaseService {
    constructor() {
        super('/api/vwClientContactsSummary', ['createdAt', 'lastTaggedAt', 'contactCreatedAt', 'lastModifiedAt']);
    }

    /**
     * Get all contacts for a specific client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<Array>} Array of contacts
     */
    async getContactsByClientId(accessToken, clientId) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}`);
        } catch (error) {
            console.error(`Failed to fetch contacts for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get active contacts for a client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<Array>} Array of active contacts
     */
    async getActiveContacts(accessToken, clientId) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/active`);
        } catch (error) {
            console.error(`Failed to fetch active contacts for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get inactive contacts for a client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<Array>} Array of inactive contacts
     */
    async getInactiveContacts(accessToken, clientId) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/inactive`);
        } catch (error) {
            console.error(`Failed to fetch inactive contacts for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get subscribed contacts for a client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<Array>} Array of subscribed contacts
     */
    async getSubscribedContacts(accessToken, clientId) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/subscribed`);
        } catch (error) {
            console.error(`Failed to fetch subscribed contacts for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get contacts with portal access for a client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<Array>} Array of contacts with portal access
     */
    async getContactsWithPortalAccess(accessToken, clientId) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/portal-access`);
        } catch (error) {
            console.error(`Failed to fetch contacts with portal access for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get contacts with tags for a client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<Array>} Array of contacts with tags
     */
    async getContactsWithTags(accessToken, clientId) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/with-tags`);
        } catch (error) {
            console.error(`Failed to fetch contacts with tags for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Search contacts by name
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {string} searchTerm - Search term for contact name
     * @returns {Promise<Array>} Array of matching contacts
     */
    async searchContactsByName(accessToken, clientId, searchTerm) {
        try {
            return await this.makeRequest(
                accessToken, 
                `/client/${clientId}/search?name=${encodeURIComponent(searchTerm)}`
            );
        } catch (error) {
            console.error(`Failed to search contacts for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get contacts ordered by name
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<Array>} Array of contacts ordered by name
     */
    async getContactsOrderedByName(accessToken, clientId) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/ordered-by-name`);
        } catch (error) {
            console.error(`Failed to fetch contacts ordered by name for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get contacts ordered by last name, first name
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<Array>} Array of contacts ordered by last name, first name
     */
    async getContactsOrderedByLastFirstName(accessToken, clientId) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/ordered-by-last-first-name`);
        } catch (error) {
            console.error(`Failed to fetch contacts ordered by last/first name for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get contacts ordered by email
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<Array>} Array of contacts ordered by email
     */
    async getContactsOrderedByEmail(accessToken, clientId) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/ordered-by-email`);
        } catch (error) {
            console.error(`Failed to fetch contacts ordered by email for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get contacts ordered by tag count
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<Array>} Array of contacts ordered by tag count (descending)
     */
    async getContactsOrderedByTagCount(accessToken, clientId) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/ordered-by-tag-count`);
        } catch (error) {
            console.error(`Failed to fetch contacts ordered by tag count for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get contacts ordered by last tagged date
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<Array>} Array of contacts ordered by last tagged date
     */
    async getContactsOrderedByLastTagged(accessToken, clientId) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/ordered-by-last-tagged`);
        } catch (error) {
            console.error(`Failed to fetch contacts ordered by last tagged for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get contacts by city
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {string} city - City name
     * @returns {Promise<Array>} Array of contacts in the specified city
     */
    async getContactsByCity(accessToken, clientId, city) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/city/${encodeURIComponent(city)}`);
        } catch (error) {
            console.error(`Failed to fetch contacts by city for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get contacts by state
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {string} stateCode - State code (e.g., 'CA', 'NY')
     * @returns {Promise<Array>} Array of contacts in the specified state
     */
    async getContactsByState(accessToken, clientId, stateCode) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/state/${encodeURIComponent(stateCode)}`);
        } catch (error) {
            console.error(`Failed to fetch contacts by state for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get contacts by contact type
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {string} contactType - Contact type name
     * @returns {Promise<Array>} Array of contacts with the specified type
     */
    async getContactsByType(accessToken, clientId, contactType) {
        try {
            return await this.makeRequest(
                accessToken, 
                `/client/${clientId}/contact-type?type=${encodeURIComponent(contactType)}`
            );
        } catch (error) {
            console.error(`Failed to fetch contacts by type for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get contact count for a client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<Object>} Count object { count: number }
     */
    async getContactCount(accessToken, clientId) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/count`);
        } catch (error) {
            console.error(`Failed to fetch contact count for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get active contact count for a client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<Object>} Count object { count: number }
     */
    async getActiveContactCount(accessToken, clientId) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/count/active`);
        } catch (error) {
            console.error(`Failed to fetch active contact count for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get contact count with tags for a client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<Object>} Count object { count: number }
     */
    async getContactCountWithTags(accessToken, clientId) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/count/with-tags`);
        } catch (error) {
            console.error(`Failed to fetch contact count with tags for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get contact by email
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {string} email - Email address
     * @returns {Promise<Object|null>} Contact object or null if not found
     */
    async getContactByEmail(accessToken, clientId, email) {
        try {
            return await this.makeRequest(
                accessToken, 
                `/client/${clientId}/email/${encodeURIComponent(email)}`
            );
        } catch (error) {
            if (error.message && error.message.includes('404')) {
                return null;
            }
            console.error(`Failed to fetch contact by email for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get specific contact
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {number} contactId - Contact ID
     * @returns {Promise<Object|null>} Contact object or null if not found
     */
    async getContact(accessToken, clientId, contactId) {
        try {
            return await this.makeRequest(accessToken, `/${clientId}/${contactId}`);
        } catch (error) {
            if (error.message && error.message.includes('404')) {
                return null;
            }
            console.error(`Failed to fetch contact ${contactId} for client ${clientId}:`, error);
            throw error;
        }
    }
}

// Create singleton instance
const vwClientContactsSummaryService = new VWClientContactsSummaryService();

// Export both named and default for flexibility
export { vwClientContactsSummaryService };
export default vwClientContactsSummaryService;
